/**
 * Search, for people who do not know the app.
 *
 * One box, prose in it, answers underneath. Everything that used to require
 * knowing where to look — whose task it is, which label it carries, which
 * project it belongs to — is offered *while typing*: `@`, `#` and `+` each
 * open a list of real names, and picking one narrows the search. Nothing has
 * to be learnt first, because the list is what teaches it.
 *
 * Three things are typed rather than picked, and the empty screen is where they
 * are taught: `"a phrase"`, `-not this`, and a task's own name. That last one
 * is the short cut this box was missing — `FEE-1` is how the work is referred
 * to in every meeting, and it now lands on that task above everything the words
 * turned up, with Enter enough to open it.
 *
 * Which names the box recognises lives in `../search-query`; what the rest of
 * the text asks for lives in `@kolibri/shared`, because the server compiles the
 * same terms. The box itself is `../search-box`, because the task list's header
 * stands the same one up over its own list. This file is the screen.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  excerpt, identifiersIn, matchesTerms, pageExcerpt, parseTerms, passesFilters, printQuery,
  termsKey, type StateGroup, type Task,
} from '@kolibri/shared';
import { Header } from '../../design-system/chrome';
import { TaskRow } from '../../../modules/work/task-parts';
import { Empty, Icon, useToast } from '../../design-system/ui';
import { Chip, chipDot } from '../../design-system/ui/chip';
import { api } from '../../sync/api';
import { useT, type TranslationKey } from '../../i18n/i18n';
import { useOpenTask } from '../../design-system/navigation';
import { byId, list, useQuery } from '../../sync/store';
import {
  narrowsByFilter, onlyWorkCanAnswer, readQuery, removeFacet, TRIGGER_OF,
  type Facet,
} from '../search-query';
import { FACET_LABEL, SearchBox, useFacetOptions, useVocabulary } from '../search-box';
// A route composes: the horizon `due = week` means is the one *My work* puts a
// figure on, and a second count of seven days is a second answer to it.
import { HORIZON_DAYS, plusDays } from '../../../modules/work/overview';
import { today } from '../../design-system/format';
import { useSession } from '../../identity/session';

interface Hit {
  kind: string;
  id: string;
  title: string;
  snippet: string;
  projectId?: string | null;
}

const KIND_ICON: Record<string, string> = {
  task: 'check', page: 'page', artboard: 'canvas', project: 'folder',
  comment: 'chat', message: 'chat', cycle: 'cycle', module: 'board', mail: 'mail',
};

const KIND_GROUP: Record<string, TranslationKey> = {
  task: 'search.groupTasks', page: 'search.groupPages', artboard: 'search.groupBoards', project: 'search.groupProjects',
  comment: 'search.groupComments', message: 'search.groupMessages',
  cycle: 'search.groupCycles', module: 'search.groupModules', mail: 'search.groupMail',
};

/** Work first, then what is written about it — and last, what arrived from outside. */
const KIND_ORDER = ['task', 'page', 'artboard', 'project', 'cycle', 'module', 'comment', 'message', 'mail'];

/**
 * A row from the server, in the shape the rest of this file uses. The rename
 * is the whole of it — and it matters, because `project_id` read as
 * `projectId` is `undefined` rather than an error, and a search narrowed to a
 * project would quietly have thrown away every full-text hit.
 */
const asHit = (row: { kind: string; id: string; title: string; snippet: string; project_id: string | null }): Hit => ({
  kind: row.kind,
  id: row.id,
  title: row.title,
  // A comment has no title of its own, so the server sends the matching line
  // as both. Printed twice it reads as a bug rather than as emphasis.
  snippet: row.snippet === row.title ? '' : row.snippet,
  projectId: row.project_id,
});

/**
 * Which project a hit belongs to.
 *
 * The index answers for most kinds and cannot for two: a comment is filed
 * against a task or a page rather than a project, and a message against a
 * channel. Both are one hop away in the local copy, and without the hop a
 * search narrowed to a project throws away every comment in it.
 */
function projectOf(hit: Hit): string | null {
  if (hit.projectId) return hit.projectId;
  if (hit.kind === 'comment') {
    const comment = byId('comment', hit.id);
    if (comment?.task_id) return byId('task', comment.task_id)?.project_id ?? null;
    if (comment?.page_id) return byId('page', comment.page_id)?.project_id ?? null;
  }
  if (hit.kind === 'message') {
    return byId('channel', byId('message', hit.id)?.channel_id)?.project_id ?? null;
  }
  return null;
}

/** The task a hit is about, for the hits that are about one. */
const taskOf = (hit: Hit): string | null => (hit.kind === 'task' ? hit.id
  : hit.kind === 'comment' ? byId('comment', hit.id)?.task_id ?? null
    : null);

/* ---------------------------------------------------------------- results */

export function Search() {
  const t = useT();
  const { workspaceId } = useSession();
  const navigate = useNavigate();
  const openTask = useOpenTask();
  const toast = useToast();
  const options = useFacetOptions();
  const vocabulary = useVocabulary();

  // The query lives in the URL, so a search can be linked, reloaded, and
  // arrived at from ⌘K with the words already in it.
  const [params, setParams] = useSearchParams();
  const urlQuery = params.get('q') ?? '';
  const [input, setInput] = useState(urlQuery);
  const written = useRef(urlQuery);

  useEffect(() => {
    if (urlQuery === written.current) return;
    written.current = urlQuery;
    setInput(urlQuery);
  }, [urlQuery]);

  useEffect(() => {
    if (input === written.current) return;
    const handle = setTimeout(() => {
      written.current = input;
      setParams(input ? { q: input } : {}, { replace: true });
    }, 400);
    return () => clearTimeout(handle);
  }, [input, setParams]);

  // One reading of the line, the same one the task header does. What this
  // screen adds is where the answers come from, not what the sentence means.
  const { text, facets, filters, errors } = useMemo(
    () => readQuery(input, options, vocabulary), [input, options, vocabulary],
  );
  const terms = useMemo(() => parseTerms(text), [text]);
  // The filter as text, which is a stable key for it and is already the one
  // answer to "do these two mean the same thing".
  const filterKey = useMemo(() => printQuery(filters, vocabulary), [filters, vocabulary]);
  const at = useMemo(
    () => ({ groupOf: (id: string) => byId('state', id)?.group_key as StateGroup | undefined, day: today(), horizon: plusDays(today(), HORIZON_DAYS) }),
    [filterKey],
  );
  const wordKey = termsKey(terms);
  /**
   * Whether anything was asked *for*, as opposed to asked against.
   *
   * `-intern` on its own reads, term by term, as "everything that does not say
   * intern" — which is almost the whole workspace, listed as though somebody
   * had searched for it. An exclusion narrows a search; it is not one. The
   * server draws the same line in `toMatch`, where a MATCH of nothing but
   * exclusions is not even valid FTS5.
   */
  const wanted = terms.some((term) => !term.negated);
  const asked = wanted || narrowsByFilter(filters);
  /** A person, a label, a state, a due date — things only work can have. */
  const workOnly = onlyWorkCanAnswer(filters);

  /**
   * The tasks the query named outright, rather than described.
   *
   * Resolved from the local mirror, which is what makes `FEE-1` land instantly
   * and offline; the server pins the same rows for anything the mirror has not
   * got. Archived work is left out here as it is everywhere else in the app —
   * a short cut into the archive would find what every list hides.
   */
  const named = useMemo(() => identifiersIn(terms), [wordKey]);
  const exact = useQuery<Task[]>(
    () => named
      .map((identifier) => list('task', (task) => task.workspace_id === workspaceId
        && !task.archived && task.identifier === identifier)[0])
      .filter((task): task is Task => Boolean(task)),
    [workspaceId, named.join(' ')],
  );
  const exactIds = exact.map((task) => task.id).join(' ');

  const tasks = useQuery(() => {
    if (!asked) return [];
    const titled = (task: Task) => matchesTerms(`${task.identifier} ${task.title}`, terms);
    return list('task', (task) => task.workspace_id === workspaceId && !task.archived
      && passesFilters(task, filters, at)
      && matchesTerms(`${task.identifier} ${task.title} ${task.description ?? ''}`, terms))
      // A word in the title beats the same word buried in a description, and
      // among equals the one somebody touched last week is the likelier one.
      .sort((a, b) => Number(titled(b)) - Number(titled(a)) || b.updated_at - a.updated_at)
      .slice(0, 50);
  }, [workspaceId, wordKey, filterKey, asked]);

  // The same filters, without the words. A comment found by the server sits on
  // a task whose *title* need not contain the search at all, so asking whether
  // that task passed the whole query would throw the comment away; asking
  // whether it passed the filters is the actual question.
  const scope = useQuery(() => {
    if (!workOnly) return null;
    return new Set(list('task', (task) => task.workspace_id === workspaceId && !task.archived
      && passesFilters(task, filters, at)).map((task) => task.id));
  }, [workspaceId, filterKey, workOnly]);

  const local = useQuery<Hit[]>(() => {
    if (!wanted || workOnly) return [];
    // A project is the one filter a page can answer, so it is the one that
    // still narrows here. Read off `filters` rather than the facets, because
    // `+Website` and `project = WEB` are the same ask and this screen no
    // longer knows which one was typed.
    const wantedProjects = new Set(filters.project ?? []);
    const inScope = (projectId: string | null) => !wantedProjects.size || (!!projectId && wantedProjects.has(projectId));
    const pages = list('page', (page) => page.workspace_id === workspaceId && !page.archived
      && inScope(page.project_id ?? null) && matchesTerms(page.title, terms)).slice(0, 12);
    const found = list('project', (project) => project.workspace_id === workspaceId && !project.archived
      && inScope(project.id)
      && matchesTerms(`${project.key} ${project.name}`, terms)).slice(0, 8);
    return [
      ...pages.map((page) => ({ kind: 'page', id: page.id, title: page.title, snippet: pageExcerpt(page.content, page.format, 90), projectId: page.project_id ?? null })),
      ...found.map((project) => ({ kind: 'project', id: project.id, title: `${project.icon ?? ''} ${project.name}`.trim(), snippet: excerpt(project.description ?? '', 90), projectId: project.id })),
    ];
  }, [workspaceId, wordKey, filterKey, workOnly, wanted]);

  const [remote, setRemote] = useState<Hit[]>([]);
  const [waiting, setWaiting] = useState(false);

  useEffect(() => {
    if (!workspaceId || !wanted || text.trim().length < 2) {
      setRemote([]);
      setWaiting(false);
      return;
    }
    setWaiting(true);
    // `live` is what keeps a slow answer to an old question from replacing a
    // fast answer to the current one — the request cannot be recalled, but its
    // result can be ignored.
    let live = true;
    const handle = setTimeout(() => {
      api.search(workspaceId, text)
        .then((response) => live && (setRemote(response.results.map(asHit)), setWaiting(false)))
        .catch(() => live && (setRemote([]), setWaiting(false)));
    }, 220);
    return () => {
      live = false;
      clearTimeout(handle);
    };
  }, [text, workspaceId, wanted]);

  const sections = useMemo(() => {
    // The named task is shown once, in its own block above these. Kept out of
    // `seen` as well as out of `hits`, so the server's copy of the same row
    // cannot walk back in underneath it.
    const pinned = new Set(exact.map((task) => task.id));
    const hits: Hit[] = [
      ...tasks
        .filter((task) => !pinned.has(task.id))
        .map((task) => ({ kind: 'task', id: task.id, title: `${task.identifier} ${task.title}`, snippet: '', projectId: task.project_id })),
      ...local,
    ];
    const seen = new Set([
      ...[...pinned].map((id) => `task:${id}`),
      ...hits.map((hit) => `${hit.kind}:${hit.id}`),
    ]);
    const projects = facets.filter((facet) => facet.kind === 'project').map((facet) => new Set(facet.ids));

    for (const hit of remote) {
      const key = `${hit.kind}:${hit.id}`;
      if (seen.has(key)) continue;
      // The index keeps archived work — the doctor checks the index against
      // the table and a hole in it is a fault. Archived work is hidden
      // everywhere else in the app, so it is hidden here too.
      if (hit.kind === 'task' && byId('task', hit.id)?.archived) continue;
      if (projects.length) {
        const where = projectOf(hit);
        if (!where || !projects.every((set) => set.has(where))) continue;
      }
      if (scope) {
        // A page has no assignee and a project carries no label, so a search
        // narrowed by one of those is a search through work: what is kept is
        // the work the filters allow, and what was written on it.
        const about = taskOf(hit);
        if (!about || !scope.has(about)) continue;
      }
      seen.add(key);
      hits.push(hit);
    }

    return KIND_ORDER
      .map((kind) => ({ kind, hits: hits.filter((hit) => hit.kind === kind) }))
      .filter((section) => section.hits.length);
  }, [tasks, local, remote, filterKey, scope, exactIds]);

  const total = exact.length + sections.reduce((sum, section) => sum + section.hits.length, 0);

  const open = (hit: Hit) => {
    if (hit.kind === 'task') openTask({ id: hit.id });
    else if (hit.kind === 'page') navigate(`/pages/${hit.id}`);
    // A board opens on its canvas: it is a thing on a page, not a page.
    else if (hit.kind === 'artboard') {
      const board = byId('artboard', hit.id);
      if (board?.page_id) navigate(`/pages/${board.page_id}`);
      else toast(t('search.boardGone'));
    }
    else if (hit.kind === 'project') navigate(`/projects/${hit.id}`);
    else if (hit.kind === 'cycle' || hit.kind === 'module') navigate(`/projects/${hit.projectId ?? ''}`);
    else if (hit.kind === 'comment') {
      const comment = byId('comment', hit.id);
      if (comment?.task_id) openTask({ id: comment.task_id });
      else if (comment?.page_id) navigate(`/pages/${comment.page_id}`);
      else toast(t('search.commentGone'));
    } else if (hit.kind === 'message') {
      // The conversation, not the message: a stream has no anchor to scroll to
      // and pretending otherwise would be a link that lands in the wrong place.
      const message = byId('message', hit.id);
      if (message?.channel_id) navigate(`/chat/${message.channel_id}`);
      else toast(t('search.messageGone'));
    } else if (hit.kind === 'mail') {
      // A mail is not in the local mirror — messages are deliberately not
      // entities — so there is nothing to look up here and the id goes in the
      // URL for the Mail screen to fetch. Which also makes the result a link
      // somebody can send.
      navigate(`/mail?open=${encodeURIComponent(hit.id)}`);
    }
  };

  return (
    <>
      <Header title={t('search.title')} />
      <div className="mx-auto max-w-[1180px] px-3 pb-20 pt-4 sm:px-6 sm:pb-16 sm:pt-5">
        <SearchBox
          autoFocus
          value={input}
          onChange={setInput}
          options={options}
          // Enter on `FEE-1` opens FEE-1. Nothing else answers to Enter, because
          // the results are already on screen: there is no "go" to press.
          onSubmit={() => exact[0] && openTask({ id: exact[0].id })}
        />

        {/* A clause that could not be resolved says so here, exactly as it does
            in the task header — one language means one answer when it is typed
            wrongly, not a sentence that is explained on one screen and silently
            ignored on the other. The rest of the line still searches. */}
        {errors.length > 0 && (
          <ul className="mb-0 mt-2 list-none p-0 text-[12.5px] text-danger">
            {errors.map((error) => (
              <li key={`${error.at}-${error.message}`} className="mb-1 flex items-start gap-1.5">
                <Icon name="bolt" size={13} />
                <span>{error.message}</span>
              </li>
            ))}
          </ul>
        )}

        {facets.length > 0 && (
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            {facets.map((facet) => (
              <FacetChip
                key={`${facet.kind}-${facet.start}`}
                facet={facet}
                onRemove={() => setInput(removeFacet(input, facet))}
              />
            ))}
          </div>
        )}

        {!asked ? (
          <Tips />
        ) : total === 0 ? (
          waiting ? (
            <p className="py-10 text-center text-muted">{t('search.searching')}</p>
          ) : (
            <Empty emoji="🫙" title={t('search.noResults', { query: input.trim() })} hint={t('search.noResultsHint')} />
          )
        ) : (
          <div className="mt-3.5">
            {workOnly && wanted && <p className="mb-2 text-[12.5px] text-muted">{t('search.workOnly')}</p>}
            {exact.length > 0 && (
              <section className="mb-4">
                <h2 className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {t('search.groupExact')}
                </h2>
                {exact.map((task) => (
                  <TaskRow key={task.id} task={task} onOpen={() => openTask({ id: task.id })} showProject />
                ))}
              </section>
            )}
            {sections.map((section) => (
              <section key={section.kind} className="mb-4">
                <h2 className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {t(KIND_GROUP[section.kind] ?? 'search.groupOther')}
                  <span className="font-normal normal-case tracking-normal">{section.hits.length}</span>
                </h2>
                {section.hits.map((hit) => <Result key={`${hit.kind}-${hit.id}`} hit={hit} onOpen={open} />)}
              </section>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/** A task is drawn the way a task is drawn everywhere else in the app. */
function Result({ hit, onOpen }: { hit: Hit; onOpen: (hit: Hit) => void }) {
  const task = hit.kind === 'task' ? byId('task', hit.id) : undefined;
  if (task) return <TaskRow task={task} onOpen={() => onOpen(hit)} showProject />;
  // The heading above already says what kind of thing this is, so the space at
  // the end of the row says where it is instead — which is the question
  // somebody looking at two pages of the same name actually has.
  const project = hit.kind === 'project' ? undefined : byId('project', projectOf(hit));
  return (
    <button type="button" className="task-row w-full text-left" onClick={() => onOpen(hit)}>
      <Icon name={KIND_ICON[hit.kind] ?? 'page'} size={15} />
      <span className="min-w-0 flex-1">
        <span className="block truncate">{hit.title || hit.snippet}</span>
        {hit.snippet && hit.title && <span className="block truncate text-[12.5px] text-muted">{hit.snippet}</span>}
      </span>
      {project && <span className="truncate text-[11.5px] text-muted">{project.name}</span>}
    </button>
  );
}

function FacetChip({ facet, onRemove }: { facet: Facet; onRemove: () => void }) {
  const t = useT();
  return (
    <Chip tone="on" className="pr-1">
      <span className="text-muted">{t(FACET_LABEL[facet.kind])}</span>
      {facet.kind === 'label' && <span className={chipDot} style={{ background: facet.color ?? 'var(--fg-muted)' }} />}
      <span className="truncate">{facet.name}</span>
      <button
        type="button"
        className="ml-0.5 grid size-6 place-items-center rounded-full hover:bg-active"
        aria-label={t('search.removeFilter', { name: facet.name })}
        onClick={onRemove}
      >
        <Icon name="close" size={12} />
      </button>
    </Chip>
  );
}

/**
 * The empty screen carries the whole feature.
 *
 * Somebody who has never used a filter in their life reads five lines here
 * and then types `@` — which is the only moment this can be taught, because
 * afterwards the screen is full of results and nobody reads it.
 *
 * The three that open a list are first, because picking beats remembering.
 * The two that are typed come after, and each is one character: a quote and a
 * minus. The task-number short cut is in the sentence above rather than in this
 * list, since it is the one line here that is about *this* workspace's own
 * vocabulary rather than about punctuation.
 */
function Tips() {
  const t = useT();
  const rows: { glyph: string; hint: TranslationKey }[] = [
    { glyph: TRIGGER_OF.person, hint: 'search.tipPerson' },
    { glyph: TRIGGER_OF.label, hint: 'search.tipLabel' },
    { glyph: TRIGGER_OF.project, hint: 'search.tipProject' },
    { glyph: '"', hint: 'search.tipPhrase' },
    { glyph: '-', hint: 'search.tipExclude' },
  ];
  return (
    <div className="mx-auto mt-10 max-w-[420px]">
      <h2 className="mb-1 text-center text-[15px] font-semibold">{t('search.promptTitle')}</h2>
      <p className="mb-4 text-center text-muted">{t('search.promptHint')}</p>
      <ul className="flex flex-col gap-1.5">
        {rows.map((row) => (
          <li key={row.glyph} className="flex items-center gap-2.5 text-[13px] text-soft">
            <span className="mono grid size-7 flex-none place-items-center rounded-[var(--radius-sm)] border border-line bg-hover text-[13px]">
              {row.glyph}
            </span>
            {t(row.hint)}
          </li>
        ))}
      </ul>
    </div>
  );
}
